import type { UIMessage } from 'ai';

export type PublicSession = {
  id: string;
  title: string;
  status: string;
  workspaceLifecycle: 'starting' | 'ready' | 'suspended' | 'resuming' | 'replacing' | 'failed' | 'terminated';
  turnState: 'idle' | 'running' | 'interrupting';
  controller: 'agent' | 'human';
  createdAt: string;
  updatedAt: string;
  lastActiveAt: string;
  pendingTask: string | null;
  originalMicrovmId: string | null;
  currentMicrovmId: string | null;
  microvmState: string | null;
  snapshotSha256: string | null;
  restoredFromMicrovmId: string | null;
  desktopGeneration: number;
  inferenceProvider: string;
  inferenceModel: string;
  inferenceProtocol: string;
  conversationGeneration: number;
  error: string | null;
};

export type Provider = {
  provider: string;
  model: string;
  credentialPresent: boolean;
};

export type RuntimeFeatures = {
  proofLab: boolean;
};

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
};

export type AuthSession =
  | {
      authenticated: false;
      googleEnabled: boolean;
      passwordEnabled: boolean;
      developmentEmailLogin: boolean;
    }
  | {
      authenticated: true;
      googleEnabled: boolean;
      passwordEnabled: boolean;
      developmentEmailLogin: boolean;
      csrfToken: string | null;
      user: AuthUser;
    };

export type CustomerDeploymentConfig = {
  enabled: boolean;
  region: string;
  stackName: string;
  templateHost: string | null;
  installationTtlMinutes: number;
  unavailableReason: string | null;
};

export type GithubInstallation = {
  installationId: string;
  accountId: number | null;
  accountLogin: string | null;
  accountType: 'Organization' | 'User' | null;
  createdAt: string;
  updatedAt: string;
};

export type GithubAppStatus = { enabled: boolean; installations: GithubInstallation[] };

export async function getGithubAppStatus(): Promise<GithubAppStatus> {
  return api<GithubAppStatus>('/api/github-app');
}

export type SlackInstallation = {
  installationId: string;
  connectedAt: string;
  responsesEnabled: boolean;
};

export type SlackAppStatus = { enabled: boolean; installations: SlackInstallation[] };

export async function getSlackAppStatus(): Promise<SlackAppStatus> {
  return api<SlackAppStatus>('/api/slack-app');
}

export type PreparedCustomerDeployment = {
  quickCreateUrl: string;
  installationId: string;
  teamId: string;
  createdAt: string;
  expiresAt: string;
  region: string;
  stackName: string;
  status: 'pending';
  statusStale: false;
};

export type CustomerDeploymentStatus = {
  installationId: string;
  teamId: string;
  status: 'pending' | 'connected' | 'disconnected' | 'expired';
  createdAt: string;
  expiresAt: string;
  lastSeenAt?: string;
  accountId?: string;
  region: string;
  stackName: string;
  vpcId?: string;
  statusStale?: boolean;
  policyRevision?: number;
  policyStatus?: 'pending' | 'deploying' | 'active' | 'failed';
  policyUpdatedAt?: string;
  githubInstallationId?: string;
  githubAccountLogin?: string;
  curatedAppCredentialSecretArn?: string;
  agentLoopStatus?: 'offline' | 'needs_openai_key' | 'ready';
  agentLoopLastSeenAt?: string;
  agentLoopConnectedWorkers?: number;
  agentLoopAvailableCapacity?: number;
};

export type CustomerAppConnection = {
  providerId: string;
  configuration: Record<string, string>;
  status: 'connected' | 'failed';
  updatedAt: string;
};

export type CustomerAppsState = {
  revision: number;
  connections: CustomerAppConnection[];
};

export type CustomerJobType = 'session.start' | 'session.prompt' | 'session.stop';
export type CustomerJobStatus =
  | 'queued'
  | 'leased'
  | 'running'
  | 'retry_wait'
  | 'succeeded'
  | 'failed'
  | 'dead_letter'
  | 'cancelling'
  | 'cancelled'
  | 'expired';
export type CustomerJobPayloadEnvelope = {
  version: 1;
  algorithm: 'RSA-OAEP-256+A256GCM';
  keyFingerprint: string;
  encryptedKey: string;
  iv: string;
  ciphertext: string;
};
export type CustomerJob = {
  jobId: string;
  installationId: string;
  sessionId: string;
  jobType: CustomerJobType;
  sourceType: string;
  status: CustomerJobStatus;
  payloadSha256: string | null;
  payloadBytes: number;
  priority: number;
  attemptCount: number;
  maximumAttempts: number;
  leaseWorkerId: string | null;
  leaseFence: number;
  leaseExpiresAt: string | null;
  notBefore: string;
  expiresAt: string;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  dismissedAt: string | null;
};
export type CustomerJobEncryption = {
  version: 1;
  algorithm: 'RSA-OAEP-256+A256GCM';
  keyId: string;
  publicKey: string;
  fingerprint: string;
};
export type CustomerJobEvent = {
  id: number;
  eventId: string;
  jobId: string;
  eventType: string;
  encryptedPayload: {
    version: 1;
    algorithm: 'A256GCM';
    iv: string;
    ciphertext: string;
  } | null;
  createdAt: string;
};
export type CustomerEncryptedPayload = {
  version: 1;
  algorithm: 'A256GCM';
  iv: string;
  ciphertext: string;
};
export type CustomerSurfaceAccessResponse = {
  accessId: string;
  encryptedAccess: CustomerEncryptedPayload;
};
export type CustomerSurfaceConnection = {
  version: 1;
  desktop: {
    url: string;
    protocols: string[];
  };
  terminal: {
    url: string;
    protocols: string[];
  };
  credentialExpiresAt: string;
};
export type CustomerJobSession = {
  sessionId: string;
  installationId: string;
  status: 'starting' | 'active' | 'stopping' | 'stopped' | 'failed';
  createdAt: string;
  updatedAt: string;
};

export type CustomerPolicy = {
  installationId: string;
  githubInstallationId: string | null;
  revision: number;
  organization: string;
  allRepositories: boolean;
  repositories: string[];
  operations: Array<'fetch' | 'push'>;
  credentialManaged: boolean;
  status: 'pending' | 'deploying' | 'active' | 'failed';
  deploymentNumber: string | null;
  updatedAt: string;
};

export async function getCustomerPolicy(installationId: string): Promise<CustomerPolicy | null> {
  return (await api<{ policy: CustomerPolicy | null }>(
    `/api/customer-deployment/installations/${encodeURIComponent(installationId)}/policy`,
  )).policy;
}

export async function updateCustomerPolicy(
  installationId: string,
  input: {
    expectedRevision: number;
    githubInstallationId: string;
    allRepositories: boolean;
    repositories: string[];
    operations: Array<'fetch' | 'push'>;
  },
): Promise<CustomerPolicy> {
  return (await api<{ policy: CustomerPolicy }>(
    `/api/customer-deployment/installations/${encodeURIComponent(installationId)}/policy`,
    { method: 'PUT', body: JSON.stringify(input) },
  )).policy;
}

export function sessionHeaders(): Record<string, string> {
  const csrfToken = csrfCookie();
  return csrfToken ? { 'x-csrf-token': csrfToken } : {};
}

function csrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rawValue] = part.trim().split('=');
    if (rawName === 'cyrus-csrf' || rawName === '__Host-cyrus-csrf') {
      return decodeURIComponent(rawValue.join('='));
    }
  }
  return null;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null;
  const response = await fetch(path, {
    ...init,
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...sessionHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
    throw new Error(problem.error ?? `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function getAuthSession(): Promise<AuthSession> {
  return api<AuthSession>('/api/auth/session');
}

export async function developmentSignIn(email: string): Promise<AuthSession> {
  await api<void>('/api/auth/development', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return getAuthSession();
}

export async function passwordSignIn(
  username: string,
  password: string,
): Promise<AuthSession> {
  await api<void>('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return getAuthSession();
}

export async function signOut(): Promise<void> {
  await api<void>('/api/auth/logout', { method: 'POST' });
}

export async function listSessions(): Promise<PublicSession[]> {
  return (await api<{ sessions: PublicSession[] }>('/api/sessions')).sessions;
}

export async function deleteSession(id: string): Promise<void> {
  await api<void>(`/api/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function getMessages(id: string): Promise<{ messages: UIMessage[]; lastEventId: number }> {
  return api<{ messages: UIMessage[]; lastEventId: number }>(`/api/sessions/${encodeURIComponent(id)}/messages`);
}

export async function getCustomerDeploymentConfig(): Promise<CustomerDeploymentConfig> {
  return api<CustomerDeploymentConfig>('/api/customer-deployment');
}

export async function createCustomerDeployment(teamId: string): Promise<PreparedCustomerDeployment> {
  return api<PreparedCustomerDeployment>('/api/customer-deployment/installations', {
    method: 'POST',
    body: JSON.stringify({ teamId }),
  });
}

export async function getCustomerDeploymentStatus(installationId: string): Promise<CustomerDeploymentStatus> {
  return api<CustomerDeploymentStatus>(
    `/api/customer-deployment/installations/${encodeURIComponent(installationId)}`,
  );
}

export async function getCurrentCustomerDeployment(): Promise<CustomerDeploymentStatus | null> {
  return (await api<CustomerDeploymentStatus | undefined>(
    '/api/customer-deployment/installations/current',
  )) ?? null;
}

export async function getCustomerApps(installationId: string): Promise<CustomerAppsState> {
  return api<CustomerAppsState>(
    `/api/customer-deployment/installations/${encodeURIComponent(installationId)}/apps`,
  );
}

export async function connectCustomerApp(input: {
  installationId: string;
  providerId: string;
  expectedRevision: number;
  configuration: Record<string, string>;
  encryptedCredential: CustomerJobPayloadEnvelope;
}): Promise<{ revision: number; connection: CustomerAppConnection }> {
  return api<{ revision: number; connection: CustomerAppConnection }>(
    `/api/customer-deployment/installations/${encodeURIComponent(input.installationId)}/apps/${encodeURIComponent(input.providerId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        expectedRevision: input.expectedRevision,
        configuration: input.configuration,
        encryptedCredential: input.encryptedCredential,
      }),
    },
  );
}

export async function launchCustomerDesktop(
  installationId: string,
): Promise<{ session: PublicSession }> {
  return api<{ session: PublicSession }>(
    `/api/customer-deployment/installations/${encodeURIComponent(installationId)}/desktop`,
    { method: 'POST' },
  );
}

export async function getCustomerJobEncryption(
  installationId: string,
): Promise<CustomerJobEncryption> {
  return api<CustomerJobEncryption>(
    `/api/customer-jobs/encryption?installationId=${encodeURIComponent(installationId)}`,
  );
}

export async function openCustomerSurface(input: {
  installationId: string;
  sessionId: string;
  encryptedRequest: CustomerJobPayloadEnvelope;
}): Promise<CustomerSurfaceAccessResponse> {
  return api<CustomerSurfaceAccessResponse>('/api/customer-surfaces', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function keepCustomerSurfaceAlive(
  accessId: string,
  installationId: string,
  sessionId: string,
): Promise<void> {
  await api<void>(
    `/api/customer-surfaces/${encodeURIComponent(accessId)}/keepalive`,
    {
      method: 'POST',
      body: JSON.stringify({ installationId, sessionId }),
    },
  );
}

export async function closeCustomerSurface(
  accessId: string,
  installationId: string,
  sessionId: string,
): Promise<void> {
  await api<void>(
    `/api/customer-surfaces/${encodeURIComponent(accessId)}/close`,
    {
      method: 'POST',
      body: JSON.stringify({ installationId, sessionId }),
    },
  );
}

export async function listCustomerJobs(
  installationId: string,
): Promise<CustomerJob[]> {
  return (await api<{ jobs: CustomerJob[] }>(
    `/api/customer-jobs?installationId=${encodeURIComponent(installationId)}`,
  )).jobs;
}

export async function listCustomerJobSessions(
  installationId: string,
): Promise<CustomerJobSession[]> {
  return (await api<{ sessions: CustomerJobSession[] }>(
    `/api/customer-job-sessions?installationId=${encodeURIComponent(installationId)}`,
  )).sessions;
}

export async function createCustomerJob(input: {
  installationId: string;
  jobType: CustomerJobType;
  sessionId?: string;
  idempotencyKey: string;
  payload?: CustomerJobPayloadEnvelope | null;
}): Promise<CustomerJob> {
  return (await api<{ job: CustomerJob }>('/api/customer-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
  })).job;
}

export async function cancelCustomerJob(jobId: string): Promise<CustomerJob> {
  return (await api<{ job: CustomerJob }>(
    `/api/customer-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  )).job;
}

export async function dismissCustomerJob(jobId: string): Promise<CustomerJob> {
  return (await api<{ job: CustomerJob }>(
    `/api/customer-jobs/${encodeURIComponent(jobId)}/dismiss`,
    { method: 'POST' },
  )).job;
}

export async function getCustomerJobEvents(
  jobId: string,
  after = 0,
): Promise<CustomerJobEvent[]> {
  return (await api<{ events: CustomerJobEvent[] }>(
    `/api/customer-jobs/${encodeURIComponent(jobId)}/events?after=${after}`,
  )).events;
}

export async function sessionAction<T = { session: PublicSession }>(
  id: string,
  action: string,
  body?: unknown,
): Promise<T> {
  return api<T>(`/api/sessions/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}
